import React, { useState, useRef, useCallback, useEffect } from 'react';

interface IsolatedInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
}

const IsolatedInput: React.FC<IsolatedInputProps> = React.memo(({
  value,
  onChange,
  placeholder,
  className,
}) => {
  const [localValue, setLocalValue] = useState(value || '');
  const isFocusedRef = useRef(false);
  const lastExternalRef = useRef(value || '');

  // Only sync from external value when:
  // 1. The input is NOT focused (user isn't actively typing), AND
  // 2. The external value actually changed
  useEffect(() => {
    if (!isFocusedRef.current && value !== lastExternalRef.current) {
      lastExternalRef.current = value || '';
      setLocalValue(value || '');
    }
  }, [value]);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setLocalValue(e.target.value);
  }, []);

  const handleFocus = useCallback(() => {
    isFocusedRef.current = true;
  }, []);

  const handleBlur = useCallback(() => {
    isFocusedRef.current = false;
    lastExternalRef.current = localValue;
    onChange(localValue);
  }, [localValue, onChange]);

  return (
    <input
      type="text"
      value={localValue}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      placeholder={placeholder}
      className={className}
    />
  );
});

IsolatedInput.displayName = 'IsolatedInput';

export default IsolatedInput;
